import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { 
  ShieldEnergyIcon, 
  Clock01Icon, 
  CreditCardIcon, 
  UserIcon,
  SparklesIcon,
  Settings01Icon
} from 'hugeicons-react'

const features = [
  {
    icon: <ShieldEnergyIcon size={32} />,
    title: 'حماية مضمونة',
    description: 'نستخدم أفضل مواد الحماية العالمية لضمان لمعان يدوم طويلاً، مع ضمان حقيقي.',
    className: "md:col-span-2 md:row-span-2",
    highlight: true
  },
  {
    icon: <Clock01Icon size={28} />,
    title: 'سرعة الإنجاز',
    description: 'نقدر وقتك، لذلك نلتزم بالمواعيد بدقة متناهية.',
    className: "md:col-span-1"
  },
  {
    icon: <CreditCardIcon size={28} />,
    title: 'أسعارنا',
    description: 'قيمة مقابل سعر لا تقبل المنافسة.',
    className: "md:col-span-1"
  },
  {
    icon: <UserIcon size={32} />,
    title: 'فريق محترف',
    description: 'خبراء معتمدون دولياً في العناية بالسيارات الفارهة.',
    className: "md:col-span-2"
  },
  {
    icon: <SparklesIcon size={28} />,
    title: 'نظافة مثالية',
    description: 'نهتم بأدق التفاصيل.',
    className: "md:col-span-1",
    accent: true
  },
  {
    icon: <Settings01Icon size={28} />,
    title: 'تتبع مباشر',
    description: 'تابع حالة سيارتك خطوة بخطوة عبر هاتفك مع تحديثات فورية.',
    className: "md:col-span-1"
  }
]

function FeatureCard({ feature, index }: { feature: typeof features[0], index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Mouse tracking values
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth springs for tilt
  const springConfig = { damping: 25, stiffness: 150 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), springConfig);

  // Glow position springs (calculating percentages safely)
  const springX = useSpring(useTransform(mouseX, [-0.5, 0.5], [0, 100]), springConfig);
  const springY = useSpring(useTransform(mouseY, [-0.5, 0.5], [0, 100]), springConfig);
  const glowX = useTransform(springX, (v) => `${v}%`);
  const glowY = useTransform(springY, (v) => `${v}%`);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      style={{ 
        rotateX, 
        rotateY,
        perspective: 1000,
        transformStyle: "preserve-3d"
      }}
      className={`
        relative p-8 rounded-3xl border border-white/5 overflow-hidden group transition-all duration-500
        ${feature.className}
        hover:border-primary/40 hover:shadow-[0_20px_50px_rgba(255,0,0,0.15)]
        ${feature.highlight ? 'bg-zinc-900/40' : 'bg-[#050505]'}
      `}
    >
      {/* Dynamic Magma Glow (cursor follow) */}
      <motion.div 
        className="absolute w-48 h-48 bg-primary/20 rounded-full blur-[60px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
        style={{
          left: glowX, // Pass motion value directly
          top: glowY,  // Pass motion value directly
          x: "-50%",
          y: "-50%",
        }}
        data-unit="percent" // Hint for framer-motion if needed
      />

      {/* Static Accent Glows */}
      {feature.highlight && (
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      )}
      
      <div className="relative z-10 h-full flex flex-col justify-between" style={{ transform: "translateZ(30px)" }}>
        <div className={`
            w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(220,20,60,0.4)]
            ${feature.accent 
                ? 'bg-primary text-white shadow-[0_0_15px_rgba(255,0,0,0.4)]' 
                : 'bg-white/5 text-gray-400 group-hover:text-primary group-hover:bg-primary/10'
            }
        `}>
          {feature.icon}
        </div>
        
        <div className="space-y-2">
            <h3 className="text-xl md:text-2xl font-black text-white group-hover:text-primary transition-colors flex items-center gap-2">
                {feature.title}
                {feature.highlight && <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse shadow-[0_0_8px_rgba(220,20,60,1)]" />}
            </h3>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed group-hover:text-gray-200 transition-colors">
                {feature.description}
            </p>
        </div>
      </div>
    </motion.div>
  );
}

export default function WhyChooseUs() {
  return (
    <section className="py-32 bg-[#020000] relative overflow-hidden">
      {/* Top Gradient Mask */}
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-[#020000] to-transparent z-10 pointer-events-none" />
      {/* Bottom Gradient Mask */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#020000] to-transparent z-10 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary font-black text-[10px] tracking-[0.3em] uppercase mb-6"
          >
            لماذا نحن؟
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-5xl md:text-7xl font-black mb-6 text-white tracking-tighter"
          >
            نحن لا ننظف، نحن <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-600">نُبدع</span>
          </motion.h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
            خبرتنا في التعامل مع أفخم السيارات جعلت منا الوجهة الأولى لعشاق التميز.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto auto-rows-[220px]">
          {features.map((feature, index) => (
            <FeatureCard key={index} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}
